import React from 'react';
import classes from './ResourceName.module.scss';
import { resourceName } from 'js/components/pipelines/cards/utils';
import { useResourceDetailHandler } from 'new/components/ResourceDetailHandler/context';
import usePaging from 'new/state/paging/repository/usePaging';
import classNames from 'classnames';
import Typography from 'new/elements/Typography';
import { Tooltip } from '@mui/material';
import { IconButton } from 'new/elements/IconButton/IconButton';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import DataSourceDetailActionsContainer from 'js/components/data_sources/cards/DataSourceDetailActionsContainer';
import DataSinkDetailActionsContainer from 'js/components/data_sinks/cards/DataSinkDetailActionsContainer';
import {
  RESOURCE_TYPE_ATTR_TRANSFORM,
  RESOURCE_TYPE_CREDENTIALS,
  RESOURCE_TYPE_DATA_MAP,
} from 'js/constants/resourceTypes';
import CredentialDetailActionsContainer from 'js/components/credentials/cards/CredentialDetailActionsContainer';
import DataMapDetailActions from 'js/components/data_maps/cards/DataMapDetailActionsContainer';
import ReusableTxDetailActionsContainer from 'js/components/reusableTransforms/cards/ReusableTxDetailActionsContainer';
import { useZoomState } from 'js/components/pipelines/FlowNodes/hooks/useZoomState';
import { StethoscopeIcon } from './StethoscopeIcon';
import { ReactComponent as CloseIcon } from 'new/components/icons/close-cross.svg';
import { ReactComponent as InspectIcon } from 'new/components/icons/fi-rr-cube.svg';
import { ReactComponent as DetailsIcon } from 'new/components/icons/fi-rr-info.svg';

const ActionsComponents = {
  DATA_SET: DataSetDetailActionsContainer,
  SOURCE: DataSourceDetailActionsContainer,
  DATA_SINK: DataSinkDetailActionsContainer,
  [RESOURCE_TYPE_CREDENTIALS]: CredentialDetailActionsContainer,
  [RESOURCE_TYPE_DATA_MAP]: DataMapDetailActions,
  [RESOURCE_TYPE_ATTR_TRANSFORM]: ReusableTxDetailActionsContainer,
};

export const ResourceName = ({
  resourceType,
  onEditButtonClicked,
  onClickExpand,
  expanded,
  isPreview,
  hideActions,
  InnerView,
  data,
  readOnly,
  hideOpenDetail,
  isManaged,
  selectActions,
  RightView,
  connectionOrVendorType,
  ActionComponent,
  children,
  isNodeDiscoveryEnabled,
  isCollapsed = false,
}: any) => {
  const name =
    children || (data?.node_type === 'splitter' ? 'Splitter' : resourceName(resourceType));
  const Actions = ActionComponent || ActionsComponents[data?.type || resourceType];
  const { openDetailDrawerInPortal } = useResourceDetailHandler();

  const { getMetaInfoById } = usePaging();
  const { zoom, isZoomedOut } = useZoomState();

  if (!data?.id && !children) return null;

  const connectionInfo = getMetaInfoById(data?.connector_type);

  const onDetailClicked = (e) => {
    e.stopPropagation();
    openDetailDrawerInPortal(data, connectionInfo);
  };

  const scale = typeof zoom === 'number' && zoom > 0 ? 1 / zoom : 1;
  const shouldShowLabel = !isZoomedOut && !isCollapsed;

  return (
    <div
      // NEX-19525: commented out dynamic resource name scaling with zoom
      // style={{
      //   transformOrigin: 'bottom right',
      //   transform: `scale(${scale >= 1 && !isZoomedOut ? scale : 1})`
      // }}
      className={classNames(classes.nameContainer, 'ResourceCard-nameContainer', {
        [classes.hidden]: !shouldShowLabel,
        [classes.visible]: shouldShowLabel,
      })}
    >
      <Typography className={classes.resourceType} variant="overline">
        {name}
        {isNodeDiscoveryEnabled ? <StethoscopeIcon /> : null}
      </Typography>

      {isPreview ? null : (
        <div className={classes.resourceNameActions}>
          {expanded ? (
            <Tooltip title="Collapse">
              <IconButton size="small" onClick={onClickExpand}>
                <CloseIcon width="12px" height="12px" />
              </IconButton>
            </Tooltip>
          ) : !!InnerView ? (
            <Tooltip title="Inspect">
              <IconButton size="small" onClick={onClickExpand}>
                <InspectIcon style={{ fill: 'white' }} width="12px" height="12px" />
              </IconButton>
            </Tooltip>
          ) : null}
          {!!openDetailDrawerInPortal && !hideOpenDetail && (
            <Tooltip title="Details">
              <IconButton size="small" onClick={onDetailClicked}>
                <DetailsIcon width="12px" height="12px" />
              </IconButton>
            </Tooltip>
          )}
          {!!Actions && !hideActions ? (
            <Actions
              showVerify={false}
              menuButtonClassName={classes.resourceActionButton}
              data={data}
              onEditButtonClicked={onEditButtonClicked}
              readOnly={readOnly}
              showStatus={false}
              showTransform={false}
              showSend={false}
              horizontalDots
              isManaged={isManaged}
              selectActions={selectActions}
              inPipeline={true}
              connectionOrVendorType={connectionOrVendorType}
            />
          ) : null}
          {RightView}
        </div>
      )}
    </div>
  );
};
